import { useMemo, useState } from 'react';
import { Maximize2 } from 'lucide-react';

import { Select } from '@/components/ui/Select';
import { cn } from '@/lib/utils';
import type { TrackedVehicle, TransportRoute } from '@/types/transport';
import { ROUTE_TONE } from '../constants';
import { RouteMap } from './RouteMap';

export interface LiveMapPanelProps {
  vehicles: TrackedVehicle[];
  routes: TransportRoute[];
  /** Given nothing, the expand control is not drawn. */
  onExpand?: () => void;
}

/**
 * The Tracking tab's board: the map full width, with the fleet filtered above
 * it and the routes named below it.
 *
 * The legend is under the plate rather than floating on it. A key that overlaps
 * the map covers the very thing it is explaining, and on a phone it would cover
 * most of it — so the plate keeps its whole area and the names sit in a row
 * that wraps.
 *
 * Both filters narrow the same list, and the map redraws from what survives —
 * so choosing one route leaves one line, which is what somebody watching a
 * single bus actually wants.
 */
export function LiveMapPanel({ vehicles, routes, onExpand }: LiveMapPanelProps) {
  const [routeCode, setRouteCode] = useState('');
  const [vehicleUuid, setVehicleUuid] = useState('');

  const visible = useMemo(
    () =>
      vehicles.filter(
        (vehicle) =>
          (!routeCode || vehicle.routeCode === routeCode) &&
          (!vehicleUuid || vehicle.uuid === vehicleUuid),
      ),
    [vehicles, routeCode, vehicleUuid],
  );

  return (
    <section className="rounded-3xl border border-line bg-surface shadow-sm">
      <header className="flex flex-col gap-3 border-b border-line px-4 py-3.5 lg:flex-row lg:items-center lg:justify-between">
        <div className="min-w-0">
          <h2 className="text-base font-bold text-ink">Live map tracking</h2>
          <p className="mt-0.5 text-sm text-ink-2">Real-time location of all active vehicles.</p>
        </div>

        <div className="flex w-full items-center gap-2 lg:w-auto">
          <div className="min-w-0 flex-1 lg:w-44 lg:flex-none">
            <Select
              label="Route"
              hideLabel
              options={[
                { value: '', label: 'All routes' },
                ...routes.map((route) => ({
                  value: route.code,
                  label: `${route.code} — ${route.name}`,
                })),
              ]}
              value={routeCode}
              onChange={(event) => {
                setRouteCode(event.target.value);
                // A vehicle chosen on another route would leave the map empty
                // and the reader wondering which control to undo.
                setVehicleUuid('');
              }}
            />
          </div>

          <div className="min-w-0 flex-1 lg:w-44 lg:flex-none">
            <Select
              label="Vehicle"
              hideLabel
              options={[
                { value: '', label: 'All vehicles' },
                ...vehicles
                  .filter((vehicle) => !routeCode || vehicle.routeCode === routeCode)
                  .map((vehicle) => ({
                    value: vehicle.uuid,
                    label: vehicle.registrationNumber,
                  })),
              ]}
              value={vehicleUuid}
              onChange={(event) => setVehicleUuid(event.target.value)}
            />
          </div>

          {onExpand && (
            <button
              type="button"
              onClick={onExpand}
              title="Expand the map"
              aria-label="Expand the map"
              className="grid h-10 w-10 shrink-0 place-items-center rounded-xl border border-line bg-surface text-ink-2 transition-colors hover:border-accent-line hover:text-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            >
              <Maximize2 className="h-4 w-4" />
            </button>
          )}
        </div>
      </header>

      <div className="p-4">
        <RouteMap vehicles={visible} className="min-h-[320px]" />

        <ul className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2">
          {routes.slice(0, 5).map((route) => (
            <li key={route.uuid} className="inline-flex items-center gap-2">
              <span
                className={cn('h-2.5 w-2.5 shrink-0 rounded-pill', ROUTE_TONE[route.tone].dot)}
                aria-hidden
              />
              <span className="text-xs font-bold text-ink-2">
                {route.code} — {route.name}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
